import { supabase } from '@/lib/supabase';
import type { ExerciseEntry } from '@/components/record/ExerciseEntryCard';
import type { Exercise } from '@/types/database';

/**
 * Fetch the user's most recent workout and return ExerciseEntry[]
 * ready to be loaded into the record form.
 */
/**
 * Fetch just the performed_at date of the last workout (for label display).
 */
export async function fetchLastWorkoutDate(userId: string): Promise<Date | null> {
  const { data, error } = await supabase
    .from('workouts')
    .select('performed_at')
    .eq('user_id', userId)
    .order('performed_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data?.performed_at) return null;
  return new Date(data.performed_at);
}

export async function fetchLastWorkoutEntries(
  userId: string,
  exerciseCatalog: Exercise[],
): Promise<ExerciseEntry[] | null> {
  const { data, error } = await supabase
    .from('workouts')
    .select('payload')
    .eq('user_id', userId)
    .order('performed_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  const pl =
    typeof data.payload === 'string' ? JSON.parse(data.payload) : data.payload;
  const exercises: any[] = pl?.exercises ?? [];
  if (exercises.length === 0) return null;

  const entries: ExerciseEntry[] = exercises.map((ex: any) => {
    const matched =
      exerciseCatalog.find((e) => e.id === ex.exercise_id) ??
      exerciseCatalog.find(
        (e) => e.name.toLowerCase() === (ex.name ?? '').toLowerCase(),
      ) ??
      null;

    const setDetails =
      (ex.sets ?? []).map((s: any) => ({
        weight: String(s.weight ?? ''),
        reps: String(s.reps ?? ''),
      })) ?? [];

    return {
      id:
        Date.now().toString() +
        Math.random().toString(36).slice(2, 6),
      exercise: matched
        ? matched
        : ({
            id: ex.exercise_id ?? '',
            name: ex.name ?? 'Unknown',
            body_part: '',
            is_custom: false,
          } as Exercise),
      weight: setDetails[0]?.weight ?? '',
      reps: setDetails[0]?.reps ?? '',
      sets: String(setDetails.length || 1),
      setDetails: setDetails.length > 0 ? setDetails : undefined,
      isPR: false,
      videoUri: null,
      notes: ex.notes ?? '',
    };
  });

  return entries;
}
